import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HandshakeIcon from '@mui/icons-material/Handshake';
import DiamondIcon from '@mui/icons-material/Diamond';

// Images
import blueprintBackground from '../../assets/images/blueprint1-right-banner.png';
import activeAirBackground from '../../assets/images/active-air-management-background.png';
import controllerImage from '../../assets/images/controller-no-shadow.png';
import monitorImage from '../../assets/images/haven-monitor.png';
import bundleImage from '../../assets/images/RMP MoCo Bundle (1).png';
import phoneImage from '../../assets/images/phone-installation-successful.png';
import proPortalImage from '../../assets/images/pro-portal-dashboard.png';
import contractorLearningBackground from '../../assets/images/contractor-learning.jpg';
import havenProLogo from '../../assets/images/HavenRGB-PRO w_grad horz.svg';
import havenCamPkgBackground from '../../assets/images/haven-cam-pkg.jpg';

// Styled components
const HeroSection = styled(Box)(({ theme }) => ({
  minHeight: '60vh',
  display: 'flex',
  alignItems: 'center',
  background: `linear-gradient(200deg, rgba(0, 196, 193, 0.7) 0%, rgba(255, 229, 35, 0.89) 100%), url(${blueprintBackground})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center right',
  backgroundRepeat: 'no-repeat',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
}));



const ActiveAirSection = styled(Box)(({ theme }) => ({
  minHeight: '60vh',
  background: `url(${activeAirBackground})`,
  backgroundSize: 'cover',
  backgroundPosition: 'top-center',
  backgroundRepeat: 'no-repeat',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 1,
  },
  '& .MuiContainer-root': {
    position: 'relative',
    zIndex: 2,
  },
}));

const TrainingSection = styled(Box)(({ theme }) => ({
  minHeight: '60vh',
  background: `url(${contractorLearningBackground})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 1,
  },
  '& .MuiContainer-root': {
    position: 'relative',
    zIndex: 2,
  },
}));

const GetStartedSection = styled(Box)(({ theme }) => ({
  minHeight: '50vh',
  background: `url(${havenCamPkgBackground})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: 1,
  },
  '& .MuiContainer-root': {
    position: 'relative',
    zIndex: 2,
  },
}));

const BecomeProPage: React.FC = () => {
  const theme = useTheme();

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h2" component="h1" sx={{ 
                fontWeight: 'bold', 
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                lineHeight: 1.2,
              }}>
                Go beyond comfort with HAVEN.
              </Typography>
              <Typography variant="h6" sx={{ 
                mb: 4,
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                lineHeight: 1.6,
                maxWidth: '600px'
              }}>
                The HAVEN ecosystem monitors IAQ, automates HVAC equipment, and empowers HVAC professionals to provide clean air services through filtration, ventilation, and humidity control.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  href="https://haveniaq.com/distributors/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: '#535f66 !important',
                    color: 'white !important',
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#3d4851 !important',
                    },
                  }}
                >
                  FIND A DISTRIBUTION LOCATION
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="https://pro.haveniaq.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  LOGIN TO PRO PORTAL
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Tagline Section */}
      <Box sx={{ backgroundColor: '#f8f9fa', py: 16 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
            <RocketLaunchIcon sx={{ 
              fontSize: '3rem',
              color: theme.palette.primary.main,
              transform: 'rotate(-45deg)'
            }} />
            <Typography variant="h3" sx={{ 
              textAlign: 'center',
              color: '#455a64',
              fontWeight: 'normal',
              fontStyle: 'italic'
            }}>
              HAVEN 'supercharges' any ducted HVAC system & IAQ accessory through an added layer of intelligence.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Active Air Management Section */}
      <ActiveAirSection sx={{ py: 8, display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" sx={{ 
            fontWeight: 'bold',
              color: 'white',
            mb: 3
          }}>
            Active Air Management
          </Typography>
          <Typography variant="h4" sx={{ 
            fontWeight: 'bold',
              color: 'white',
            mb: 4
          }}>
            Be more than just your customer's technician, become the custodian of their comfort.
          </Typography>
          <Typography variant="h5" sx={{ 
              color: 'white',
            mb: 3,
            fontWeight: 'bold'
          }}>
            What is Active Air Management?
          </Typography>
          <Typography variant="body1" sx={{ 
              color: 'white',
            lineHeight: 1.8,
            fontSize: '1.1rem',
            maxWidth: '800px',
            mx: 'auto'
          }}>
            Active Air Management is the proactive & ongoing care of your customer's indoor environment through software services and equipment automation.
          </Typography>
        </Box>
      </Container>
      </ActiveAirSection>

      {/* HAVEN Toolbox Header Section */}
      <Box sx={{ backgroundColor: '#535f66', py: 4 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ 
            textAlign: 'center',
            fontWeight: 'bold',
            color: 'white'
          }}>
            THE HAVEN TOOLBOX
          </Typography>
        </Container>
      </Box>

      {/* HAVEN Controller Section */}
      <Box sx={{ backgroundColor: '#9bb3bf', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* Controller Text */}
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h3" sx={{ 
                    fontWeight: 'bold',
                    mb: 3,
                color: 'white'
                  }}>
                    HAVEN Central Air Controller
                  </Typography>
                  <Typography variant="body1" sx={{ 
                color: 'white',
                    lineHeight: 1.6,
                mb: 3,
                fontSize: '1.1rem'
                  }}>
                    This piece of the puzzle closes the loop to make HVAC equipment automations possible, right out of the box. Think Demand Filtration, Demand Ventilation, Demand Humidity Control.
                  </Typography>
              <Typography variant="body1" sx={{ 
                color: 'white',
                lineHeight: 1.6,
                fontSize: '1.1rem'
                  }}>
                    Every HAVEN Bundle comes with 1 Controller that can accommodate 2 different pieces of equipment. Supplemental Controllers can be purchased separately.
                  </Typography>
            </Grid>

            {/* Controller Image */}
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={controllerImage} 
                  alt="HAVEN Controller" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '400px',
                    height: 'auto'
                  }} 
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* HAVEN Monitor Section */}
      <Box sx={{ backgroundColor: theme.palette.primary.main, py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* Monitor Image */}
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={monitorImage} 
                  alt="HAVEN Central Air Monitor" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '400px',
                    height: 'auto'
                  }} 
                />
                  </Box>
            </Grid>

            {/* Monitor Text */}
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h3" sx={{ 
                    fontWeight: 'bold',
                    mb: 3,
                color: 'white'
                  }}>
                    HAVEN Central Air Monitor
                  </Typography>
                  <Typography variant="body1" sx={{ 
                color: 'white',
                    lineHeight: 1.6,
                fontSize: '1.1rem'
                  }}>
                    Every HAVEN journey starts by installing the Central Air Monitor, one of which is included in every HAVEN Bundle. The only IAQ solution of its kind, the Monitor is able to detect airborne particles, chemicals, relative humidity, temperature and airflow — all from the return duct.
                  </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* HAVEN Ecosystem Section */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* Ecosystem Text */}
            <Grid item xs={12} md={6}>
          <Typography variant="h3" component="h2" sx={{ 
            fontWeight: 'bold',
            color: '#455a64',
            mb: 4
          }}>
            The HAVEN Ecosystem
          </Typography>
          <Typography variant="body1" sx={{ 
            color: '#666',
            lineHeight: 1.8,
            fontSize: '1.1rem',
            mb: 4
          }}>
            Together, the HAVEN Monitor and Controller enable real-time IAQ automations for Active Air Management: empowering HVAC professionals to provide clean air services through filtration, ventilation and humidity control.
          </Typography>
          <Button
            variant="outlined"
            size="large"
                href="https://drive.google.com/file/d/1eCM3-RxdNZfgaPlvjFvNoYncNvIDHqwa/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
            sx={{
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              '&:hover': {
                borderColor: theme.palette.primary.dark,
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              },
            }}
          >
            DOWNLOAD SPEC SHEETS
          </Button>
            </Grid>

            {/* Bundle Image */}
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={bundleImage} 
                  alt="HAVEN Monitor and Controller Bundle" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '350px',
                    height: 'auto',
                    imageRendering: 'crisp-edges'
                  }} 
                />
        </Box>
            </Grid>
          </Grid>
      </Container>
      </Box>

      {/* HAVEN IAQ App Section */}
      <Box sx={{ backgroundColor: '#FFE529', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* Phone Image */}
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={phoneImage} 
                  alt="HAVEN IAQ App Installation Success" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '300px',
                    height: 'auto'
                  }} 
                />
              </Box>
            </Grid>

            {/* App Text */}
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h3" sx={{ 
                    fontWeight: 'bold',
                    mb: 3,
                color: '#455a64'
                  }}>
                    HAVEN IAQ App
                  </Typography>
                  <Typography variant="body1" sx={{ 
                color: '#455a64',
                    lineHeight: 1.6,
                fontSize: '1.1rem',
                mb: 4
                  }}>
                    Your fail-safe friend at any HAVEN installation. Get the hardware connected to your customer's WiFi, having the validation that the installations were completed successfully.
                  </Typography>
                  <Button
                variant="outlined"
                size="large"
                href="mailto:sales@haveniaq.com"
                    sx={{
                  borderColor: '#455a64',
                  color: '#455a64',
                      fontWeight: 'bold',
                  px: 4,
                  py: 1.5,
                      '&:hover': {
                    borderColor: '#455a64',
                    backgroundColor: '#455a64',
                    color: 'white',
                      },
                    }}
                  >
                    REQUEST A DEMO
                  </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* HAVEN Pro Portal Section */}
      <Box sx={{ backgroundColor: '#535f66', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* Portal Text */}
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h3" sx={{ 
                    fontWeight: 'bold',
                    mb: 3,
                color: 'white'
                  }}>
                    HAVEN Pro Portal
                  </Typography>
                  <Typography variant="body1" sx={{ 
                color: 'white',
                    lineHeight: 1.6,
                fontSize: '1.1rem',
                    mb: 3
                  }}>
                    Auto-sorted customer lists based on filtration, ventilation and relative humidity issues help you identify data-driven sales opportunities your customers can trust.
                  </Typography>
                  <Typography variant="body1" sx={{ 
                color: 'white',
                    lineHeight: 1.6,
                fontSize: '1.1rem',
                mb: 4
                  }}>
                    Cultivate longterm relationships by delivering HAVEN IAQ and HVAC performance reports as part of your maintenance contract services.
                  </Typography>
                  <Button
                    variant="outlined"
                size="large"
                href="https://drive.google.com/file/d/1t88OH2FTC4A5SComlwom1fQutFkRqpTP/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                    sx={{
                  borderColor: 'white',
                  color: 'white',
                      fontWeight: 'bold',
                  px: 4,
                  py: 1.5,
                      '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'white',
                    color: '#535f66',
                      },
                    }}
                  >
                    DOWNLOAD SPEC SHEET
                  </Button>
            </Grid>

            {/* Portal Dashboard Image */}
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={proPortalImage} 
                  alt="HAVEN Pro Portal Dashboard" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '500px',
                    height: 'auto'
                  }} 
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Training & Support Section */}
      <TrainingSection sx={{ py: 8, display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* Logo */}
            <Grid item xs={12} sx={{ textAlign: 'center', mb: 4 }}>
              <img 
                src={havenProLogo} 
                alt="HAVEN Pro Logo" 
                style={{ 
                  width: '100%', 
                  maxWidth: '400px',
                  height: 'auto'
                }} 
              />
            </Grid>

            {/* Training Content */}
            <Grid item xs={12}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" sx={{ 
            fontWeight: 'bold',
                  color: 'white',
            mb: 4
          }}>
            HAVEN Pro Training & Support
          </Typography>
          <Typography variant="body1" sx={{ 
                  color: 'white',
            lineHeight: 1.8,
            fontSize: '1.1rem',
            maxWidth: '800px',
            mx: 'auto',
            mb: 4
          }}>
            You'll have a dedicated Account Manager and Support team to get you started. They're also there to help you find a go-to-market strategy for making HAVEN part of your HVAC business, leveraging it to choose more effective solutions for your customers.
          </Typography>
          <Typography variant="body1" sx={{ 
                  color: 'white',
            lineHeight: 1.8,
            fontSize: '1.1rem',
            maxWidth: '800px',
            mx: 'auto',
            mb: 4
          }}>
            Our online resources are ready whenever you get that next coffee break — on a roof, in your truck, or in between gigs at the office.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
                    href="https://pro.haveniaq.com"
                    target="_blank"
                    rel="noopener noreferrer"
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                fontWeight: 'bold',
                px: 4,
                py: 1.5,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              BECOME A PRO
            </Button>
            <Button
              variant="outlined"
              size="large"
                    href="https://haveniaq.com/contact/"
                    target="_blank"
                    rel="noopener noreferrer"
              sx={{
                      borderColor: 'white',
                      color: 'white',
                fontWeight: 'bold',
                px: 4,
                py: 1.5,
                '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              GET PRO SUPPORT
            </Button>
          </Box>
        </Box>
            </Grid>
          </Grid>
      </Container>
      </TrainingSection>

      {/* Benefits Section */}
      <Box sx={{ backgroundColor: '#f8f9fa', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ 
            textAlign: 'center',
            fontWeight: 'bold',
            color: '#455a64',
            mb: 6
          }}>
            Benefits of becoming a HAVEN Pro:
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <AssessmentIcon sx={{ 
                  fontSize: '3rem',
                  color: '#455a64',
                  mb: 2
                }} />
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: '#455a64',
                  mb: 2
                }}>
                  Increase solution sales through tailored IAQ reports
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <HandshakeIcon sx={{ 
                  fontSize: '3rem',
                  color: '#455a64',
                  mb: 2
                }} />
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: '#455a64',
                  mb: 2
                }}>
                  Improve customer retention through industry-leading IAQ services
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <DiamondIcon sx={{ 
                  fontSize: '3rem',
                  color: '#455a64',
                  mb: 2
                }} />
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: '#455a64',
                  mb: 2
                }}>
                  Enhance customer satisfaction with demand filtration, ventilation and humidity control
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Help Desk Articles Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" sx={{ 
          textAlign: 'center',
          fontWeight: 'bold',
          color: theme.palette.primary.main,
          mb: 6
        }}>
          Most Popular Help Desk Articles for Pros
        </Typography>
        
        <Grid container spacing={4}>
          {/* Central Air Monitor */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              height: '100%',
              minHeight: '400px'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Grid container spacing={3} alignItems="center">
                  {/* Device Image */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <img 
                        src={monitorImage} 
                        alt="HAVEN Central Air Monitor" 
                        style={{ 
                          width: '100%', 
                          maxWidth: '120px',
                          height: 'auto'
                        }} 
                      />
                    </Box>
                  </Grid>
                  
                  {/* Device Title */}
                  <Grid item xs={12} md={8}>
                    <Typography variant="h5" sx={{ 
                  fontWeight: 'bold',
                      color: 'white',
                      mb: 3
                }}>
                  HAVEN Central Air Monitor
                </Typography>
                  </Grid>
                </Grid>

                {/* Links */}
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}>
                      <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
                        ?
                      </Typography>
                    </Box>
                    <Button 
                      href="https://support.pro.haveniaq.com/hc/en-us/articles/360061667594-How-to-install-the-HAVEN-Central-Air-Monitor"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        color: 'white', 
                        textTransform: 'none',
                        fontWeight: 'bold',
                        justifyContent: 'flex-start',
                        p: 0
                      }}
                    >
                      HOW TO INSTALL
                    </Button>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}>
                      <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
                        i
                      </Typography>
                    </Box>
                    <Button 
                      href="https://support.pro.haveniaq.com/hc/en-us/search/click?data=BAh7DjoHaWRsKwip2XTVUwA6D2FjY291bnRfaWRpAxo8ojoJdHlwZUkiDGFydGljbGUGOgZFVDoIdXJsSSJpaHR0cHM6Ly9zdXBwb3J0LnByby5oYXZlbmlhcS5jb20vaGMvZW4tdXMvYXJ0aWNsZXMvMzYwMDYzNDkwNDczLUhBVkVOLUNlbnRyYWwtQWlyLU1vbml0b3ItU29mdC1SZXNldAY7CFQ6DnNlYXJjaF9pZEkiKWRkNTMzZGQ1LTNkNDYtNGQ0MC04MmM4LWU2OWE5MjMwYTY0OAY7CEY6CXJhbmtpCToLbG9jYWxlSSIKZW4tdXMGOwhUOgpxdWVyeUkiCnJlc2V0BjsIVDoScmVzdWx0c19jb3VudGkW--dba0d9f02aef71ac07c882dd0c30c8dfa12e6fd2"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        color: 'white', 
                        textTransform: 'none',
                        fontWeight: 'bold',
                        justifyContent: 'flex-start',
                        p: 0
                      }}
                    >
                      HOW TO SOFT RESET
                    </Button>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}>
                      <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
                        !
                      </Typography>
                    </Box>
                    <Button 
                      href="https://support.pro.haveniaq.com/hc/en-us/articles/1500003066361-HAVEN-Central-Air-Monitor-Hard-Reset"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        color: 'white', 
                        textTransform: 'none',
                        fontWeight: 'bold',
                        justifyContent: 'flex-start',
                        p: 0
                      }}
                    >
                      HOW TO HARD RESET
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Central Air Controller */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              backgroundColor: '#455a64',
              color: 'white',
              height: '100%',
              minHeight: '400px'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Grid container spacing={3} alignItems="center">
                  {/* Device Image */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <img 
                        src={controllerImage} 
                        alt="HAVEN Central Air Controller" 
                        style={{ 
                          width: '100%', 
                          maxWidth: '120px',
                          height: 'auto'
                        }} 
                      />
                    </Box>
                  </Grid>
                  
                  {/* Device Title */}
                  <Grid item xs={12} md={8}>
                    <Typography variant="h5" sx={{ 
                  fontWeight: 'bold',
                      color: 'white',
                      mb: 3
                }}>
                  HAVEN Central Air Controller
                </Typography>
                  </Grid>
                </Grid>

                {/* Links */}
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}>
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                        ?
                      </Typography>
                    </Box>
                    <Button 
                      href="https://support.pro.haveniaq.com/hc/en-us/articles/1500010374841-How-to-install-the-HAVEN-Central-Air-Controller"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        color: 'white', 
                        textTransform: 'none',
                        fontWeight: 'bold',
                        justifyContent: 'flex-start',
                        p: 0
                      }}
                    >
                      HOW TO INSTALL
                    </Button>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}>
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                        i
                      </Typography>
                    </Box>
                    <Button 
                      href="https://support.pro.haveniaq.com/hc/en-us/search/click?data=BAh7DjoHaWRsKwiTDa3qAAQ6D2FjY291bnRfaWRpAxo8ojoJdHlwZUkiDGFydGljbGUGOgZFVDoIdXJsSSJtaHR0cHM6Ly9zdXBwb3J0LnByby5oYXZlbmlhcS5jb20vaGMvZW4tdXMvYXJ0aWNsZXMvNDQwMTk4MzcyMDg1MS1IQVZFTi1DZW50cmFsLUFpci1Db250cm9sbGVyLVNvZnQtUmVzZXQGOwhUOg5zZWFyY2hfaWRJIilkZDUzM2RkNS0zZDQ2LTRkNDAtODJjOC1lNjlhOTIzMGE2NDgGOwhGOglyYW5raQc6C2xvY2FsZUkiCmVuLXVzBjsIVDoKcXVlcnlJIgpyZXNldAY7CFQ6EnJlc3VsdHNfY291bnRpFg%3D%3D--682282884719d63b8e0990914a133b740d747107"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        color: 'white', 
                        textTransform: 'none',
                        fontWeight: 'bold',
                        justifyContent: 'flex-start',
                        p: 0
                      }}
                    >
                      HOW TO SOFT RESET
                    </Button>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}>
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                        !
                      </Typography>
                    </Box>
                    <Button 
                      href="https://support.pro.haveniaq.com/hc/en-us/articles/4410639666323-Controlling-Your-Blower-Motor-with-the-HAVEN-Central-Air-Controller"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        color: 'white', 
                        textTransform: 'none',
                        fontWeight: 'bold',
                        justifyContent: 'flex-start',
                        p: 0
                      }}
                    >
                      CONTROL YOUR BLOWER MOTOR
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Get Started Section */}
      <GetStartedSection sx={{ py: 8, display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
              href="https://haveniaq.com/haven-iaq-start"
              target="_blank"
              rel="noopener noreferrer"
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
                fontSize: '1.1rem',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            GET STARTED WITH HAVEN
          </Button>
        </Box>
      </Container>
      </GetStartedSection>
    </Box>
  );
};

export default BecomeProPage; 